import { useEffect, useState } from "react";
import { getCurrentTempByPostcode } from "./weather";

export interface WeatherState {
  tempC: number | null;
  loading: boolean;
  error: string | null;
  postcode: string | null;
}

// In-memory cache by postcode for the current page session. The user
// rarely re-opens the same form within minutes, and open-meteo is fast
// enough that a long TTL isn't needed.
const CACHE = new Map<string, { tempC: number; ts: number }>();
const TTL_MS = 30 * 60 * 1000; // 30 minutes

export function useWeather(postcode: string | null | undefined): WeatherState {
  const [tempC, setTempC] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const code = postcode ? postcode.trim() : null;

  useEffect(() => {
    if (!code) {
      setTempC(null);
      setLoading(false);
      setError(null);
      return;
    }

    const cached = CACHE.get(code);
    if (cached && Date.now() - cached.ts < TTL_MS) {
      setTempC(cached.tempC);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    getCurrentTempByPostcode(code)
      .then((result) => {
        if (cancelled) return;
        if (result) {
          CACHE.set(code, { tempC: result.tempC, ts: Date.now() });
          setTempC(result.tempC);
        } else {
          setError("Couldn't look up the weather for that postcode.");
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Weather lookup failed. Check your connection.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  return { tempC, loading, error, postcode: code };
}
